/**
 * ArchitectureStandardsView (P399). Route: /twin/architecture-standards.
 *
 * Conformance against a curated catalogue of structural standards,
 * derived continuously from the estate rather than collected by an
 * annual survey answered by the people being measured.
 *
 * Each standard shows the rule in words and why a bank holds it, so a
 * breach can be argued with on the merits. Breaching standards lead;
 * conformant ones stay visible, because a standard reporting
 * conformance is doing its job and hiding it would make the catalogue
 * look like a list of problems rather than a list of assertions.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { api } from "shared/api/client";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { canRaiseFindingAgainst, raiseFindingPath } from "shared/findings/subjectKinds";
import { layerItemPath } from "shared/routes/entityRoutes";

interface StandardBreachOut {
  entity_kind: string;
  entity_id: string;
  entity_name: string;
  detail: string;
}

interface StandardOut {
  key: string;
  title: string;
  statement: string;
  rationale: string;
  conformant: boolean;
  status: "breach" | "conformant" | "not_assessable";
  assessed: number;
  breaches: StandardBreachOut[];
}

interface StandardsReportOut {
  summary: {
    standards_total: number;
    conformant: number;
    breaching: number;
    not_assessable: number;
    breaches_total: number;
  };
  standards: StandardOut[];
}

/**
 * A standard with NOTHING TO ASSESS must not read green. "No fourth
 * party is concentrated" on an estate with no modelled fourth parties
 * is true and worthless -- a clean bill of health for a question never
 * asked. Same reasoning as hiding the board's tolerance line when no
 * tolerance is declared.
 */
const STATUS_TONE: Record<string, string> = {
  breach: "bg-rose-100 text-rose-800",
  conformant: "bg-emerald-100 text-emerald-800",
  not_assessable: "bg-slate-200 text-slate-700",
};

export function ArchitectureStandardsView() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);

  const report = useQuery({
    queryKey: ["twin", "architecture-standards"],
    queryFn: () => api.get<StandardsReportOut>("/v1/twin/architecture-standards"),
  });
  const p = report.data;

  return (
    <article data-testid="architecture-standards-view" className="max-w-4xl space-y-5">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="capability-map"
            className="me-2 inline-block h-6 w-6 align-[-4px]"
          />
          {t("architectureStandards.heading")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500">
          {t("architectureStandards.body")}
        </p>
      </header>

      {report.isError && (
        <p className="text-sm text-rose-700">{t("architectureStandards.error")}</p>
      )}

      {p && (
        <>
          <section
            data-testid="architecture-standards-summary"
            className="flex flex-wrap gap-2 text-sm"
          >
            <span className="rounded border border-paper-2 bg-white px-2 py-1">
              {t("architectureStandards.total", { count: p.summary.standards_total })}
            </span>
            <span className="rounded bg-emerald-100 px-2 py-1 text-emerald-800">
              {p.summary.conformant} {t("architectureStandards.conformant")}
            </span>
            <span className="rounded bg-rose-100 px-2 py-1 text-rose-800">
              {p.summary.breaching} {t("architectureStandards.breaching")}
            </span>
            {p.summary.not_assessable > 0 && (
              <span
                data-testid="architecture-standards-not-assessable"
                className="rounded bg-slate-200 px-2 py-1 text-slate-700"
              >
                {p.summary.not_assessable} {t("architectureStandards.notAssessable")}
              </span>
            )}
          </section>

          <ul className="space-y-2">
            {p.standards.map((s) => (
              <li
                key={s.key}
                data-testid={`architecture-standard-${s.key}`}
                data-conformant={s.conformant}
                data-status={s.status}
                className="rounded border border-paper-2 bg-white p-3"
              >
                <div className="flex flex-wrap items-baseline gap-2">
                  <span
                    data-testid={`architecture-standard-badge-${s.key}`}
                    className={`rounded px-1.5 py-0.5 text-xs ${STATUS_TONE[s.status]}`}
                  >
                    {s.status === "breach"
                      ? t("architectureStandards.breachCount", {
                          count: s.breaches.length,
                        })
                      : t(`architectureStandards.${s.status}Badge`)}
                  </span>
                  <span className="font-medium text-slate-900">{s.title}</span>
                  <span className="text-xs text-slate-400">
                    {t("architectureStandards.assessed", { count: s.assessed })}
                  </span>
                </div>
                <p className="mt-1 text-sm text-slate-700">{s.statement}</p>
                <p className="mt-1 text-xs italic text-slate-500">{s.rationale}</p>

                {s.breaches.length > 0 && (
                  <>
                    <button
                      type="button"
                      data-testid={`architecture-standard-toggle-${s.key}`}
                      onClick={() => setOpen(open === s.key ? null : s.key)}
                      className="mt-2 text-xs text-brand-brassDark hover:underline"
                    >
                      {open === s.key
                        ? t("architectureStandards.hideRows")
                        : t("architectureStandards.showRows", {
                            count: s.breaches.length,
                          })}
                    </button>
                    {open === s.key && (
                      <ul
                        data-testid={`architecture-standard-rows-${s.key}`}
                        className="mt-1.5 space-y-1"
                      >
                        {s.breaches.map((b) => {
                          /* Round 9 F1 -- a breach can become work.
                             P399 found F5 standing behind twelve
                             MVB-supporting services, which is a single
                             point of failure no service-by-service
                             view can show -- and then the page
                             stopped. A standard that states a breach
                             and offers nothing to do about it is an
                             observation.

                             Round 10 F1 -- that link was offered on
                             every row regardless of kind, and a
                             finding's subject is a closed set. Five of
                             the estate's sixty-seven breaching rows
                             are facilities, contracts and data assets,
                             which cannot carry one: the form would
                             have refused them. Those rows lead to the
                             record instead, and where there is no
                             record either the row says so rather than
                             offering a button that cannot work. */
                          const raise = raiseFindingPath(b.entity_kind, b.entity_id);
                          /* `layerItemPath`, not `flagDetailPath`: a
                             data asset has no page of its own but does
                             have a register that opens on it, and the
                             layer map is where that second fallback
                             already lives. Using the narrower helper
                             left the data-residency rows with no route
                             at all -- found by driving the live estate,
                             where they are the only rows of that
                             kind. */
                          const record = layerItemPath(b.entity_kind, b.entity_id);
                          return (
                            <li
                              key={b.entity_id}
                              data-testid={`architecture-standard-row-${b.entity_id}`}
                              data-entity-kind={b.entity_kind}
                              className="rounded bg-paper-2 px-2 py-1 text-xs text-slate-700"
                            >
                              <span className="font-medium">{b.entity_name}</span>
                              {" — "}
                              {b.detail}{" "}
                              {raise ? (
                                <Link
                                  to={raise}
                                  data-testid={`architecture-standard-raise-${b.entity_id}`}
                                  className="text-brand-brassDark hover:underline"
                                >
                                  {t("architectureStandards.raise")}
                                </Link>
                              ) : record ? (
                                <Link
                                  to={record}
                                  data-testid={`architecture-standard-record-${b.entity_id}`}
                                  className="text-brand-brassDark hover:underline"
                                >
                                  {t("architectureStandards.openRecord")}
                                </Link>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {open === s.key &&
                      s.breaches.every(
                        (b) => !canRaiseFindingAgainst(b.entity_kind),
                      ) && (
                        <p
                          data-testid={`architecture-standard-nofinding-${s.key}`}
                          className="mt-1 text-xs italic text-slate-500"
                        >
                          {t("architectureStandards.notAFindingSubject")}
                        </p>
                      )}
                  </>
                )}
              </li>
            ))}
          </ul>

          <p className="border-t border-paper-2 pt-3 text-xs text-slate-500">
            {t("architectureStandards.boundary")}
          </p>
        </>
      )}
    </article>
  );
}
